import React from 'react';
import moment from 'moment';
import { useWindowSize } from 'usehooks-ts';

import { useUserContext } from '../../context/UserContext';
import { usePlayerContext } from '../../context/PlayerContext';
import { useStreamContext } from '../../context/StreamContext';
import { useLocaleContext } from '../../context/LocaleContext';
import { useModalContext } from '../../context/ModalContext';
import { useEventContext } from '../../context/EventContext';
import { useNavigate } from "react-router-dom";
import { generateRealIntervals, unixDiff } from '../../helpers/times';
import { getBroadcastTimeline, getEpisodeStatus, getEpisodesByDate } from '../../helpers/schedule-helper';
import { useHover } from '../../hooks/useHover';
import { pageByType } from '../../helpers/page-helpers';
import storage from '../../../../player/js/storage';
import { episodeLikeToggle } from '../../utils/api';

import { EPISODE_STATUSES, RECORD_STATUSES, TYPE_HYBRID } from '../../constants/common';
import { MINUTE_SIZE } from '../../components/HallEpisodes/constants';
import { TYPE_PROGRAM } from '../../constants/page-types';

import { CButton } from '../../components/UI';
import DateTabs from '../../components/HallEpisodes/DateTabs';
import SlideControl from '../../components/HallEpisodes/Controls/SlideControl';
import ZoomControl from '../../components/HallEpisodes/Controls/ZoomControl';
import Schedule from '../../components/HallEpisodes/Schedule';
import ScheduleCards from '../../components/HallEpisodes/Cards';
import Progress from '../../components/HallEpisodes/Progress';
import ScheduleDetailsModal from '../../components/Schedule/ScheduleDetailsModal';
import { useApplicationContext } from '../../context/ApplicationContext';
import ScheduleEpisodes from '../../components/Schedule';
import { ScheduleContextProvider } from '../../context/ScheduleContext';
import UserAuthenticationModal from '../../components/User/UserAuthenticationModal';
import { useWidgetContext } from '../../context/WidgetContextProvider';

import './style.scss';

const SCHEDULE_SHIFT_TIMEOUT = 5000;

export default ({ block, page }) => {
    const { event, pages, watchKey } = useEventContext();
    const {
        setStream: setPlayerStream,
        currentEpisode: playerEpisode,
        isShowPlayerHalls,
        embedEpisode,
    } = usePlayerContext();
    const { participationType, user } = useUserContext();
    const { streams, stream: currentStream, recordStatus } = useStreamContext();
    const $locale = useLocaleContext();
    const modalContext = useModalContext();
    const navigate = useNavigate();
    const { handleBookmark, bookmarkEpisodeIds } = useApplicationContext();
    const { isWidget } = useWidgetContext();

    const scheduleRef = React.useRef(null);
    const zoomControlRef = React.useRef(null);
    const progressRef = React.useRef(null);
    const slideControlRef = React.useRef(null);
    const shiftTimeoutRef = React.useRef(null);

    const [streamDate, setStreamDate] = React.useState(null);
    const [zoomValue, setZoomValue] = React.useState(block?.pixelsInMinute ?? MINUTE_SIZE);
    const [slideOffset, setSlideOffset] = React.useState(0);
    const [isScheduleMove, setIsScheduleMove] = React.useState(false);
    const [isActiveFavorites, setIsActiveFavorites] = React.useState(false);

    const { width: windowSize = 0 } = useWindowSize();
    const isScheduleHover = useHover([scheduleRef, zoomControlRef, slideControlRef]);

    const dateEpisodes = React.useMemo(() => {
        if (!currentStream || !streamDate) return null;

        return getEpisodesByDate(streamDate, currentStream.episode.filter((episode) =>
            !episode.hidden &&
            (!episode.participationType || episode.participationType === TYPE_HYBRID || episode.participationType === participationType)
        ));
    }, [currentStream, streamDate, participationType]);

    React.useEffect(() => {
        if (scheduleRef.current) {
            scheduleRef.current.scrollLeft = slideOffset;
        }
    }, [slideOffset]);

    React.useEffect(() => {
        if (block?.pixelsInMinute > 0) {
            setZoomValue(block.pixelsInMinute);
        }
    }, [block?.pixelsInMinute]);

    const timeline = React.useMemo(() => {
        if (!dateEpisodes?.length) return null;

        return modifyTimeLine(getBroadcastTimeline(dateEpisodes));
    }, [dateEpisodes, zoomValue, windowSize]);

    function modifyTimeLine(timeline) {
        const intervalWidth = timeline?.intervals.reduce((acc, unix, index, arr) => {
            if (index === arr.length - 1) return acc;
            acc += unixDiff(unix, arr[index + 1]) * zoomValue;
            return acc;
        }, 0);

        const refWidth = scheduleRef.current?.getBoundingClientRect()?.width ?? null;

        if (!refWidth) return timeline;

        const offset = Math.round(refWidth) - intervalWidth;

        if (offset < 0) return timeline;

        const minutes = Math.floor(offset / zoomValue);
        const newtimeStop = moment.unix(timeline.timeStop).add(minutes, 'minutes').unix();
        const newIntervals = generateRealIntervals(timeline.timeStart, newtimeStop);

        return {
            ...timeline,
            timeStart: timeline.timeStart,
            timeStop: newtimeStop,
            intervals: newIntervals,
        };
    }

    function handleUpdateProgress(progress) {
        if (!progressRef.current || isScheduleHover || !!modalContext.modalRef.current || isScheduleMove) {
            if (shiftTimeoutRef.current) {
                clearTimeout(shiftTimeoutRef.current);
                shiftTimeoutRef.current = null;
            }

            shiftTimeoutRef.current = setTimeout(() => {
                clearTimeout(shiftTimeoutRef.current);
                shiftTimeoutRef.current = null;
            }, SCHEDULE_SHIFT_TIMEOUT);
            setIsScheduleMove(false);
        } else {
            if (shiftTimeoutRef.current) return;

            const { clientWidth } = progressRef.current.parentElement;

            setSlideOffset(progress.left - (clientWidth / 2));
        }
    }

    function handleEpisodeClick(episode) {
        if (!episode) return;

        const episodeStatus = getEpisodeStatus(episode);

        if ((page.type === 'broadcast' || isShowPlayerHalls) && currentStream.dvr
            && (recordStatus === RECORD_STATUSES.EPISODES_CUT
                || episodeStatus === EPISODE_STATUSES.record
                || episodeStatus === EPISODE_STATUSES.live
            )
        ) {
            setPlayerStream(currentStream, episode);
            document.querySelector('#player-block')?.scrollIntoView({ block: 'center' });

            return;
        }

        if (!block.showModal) return;

        modalContext?.renderModal(
            ScheduleDetailsModal,
            {
                episode: { stream: currentStream, ...episode },
                settings: pageByType(pages, TYPE_PROGRAM)?.settings ?? {},
                lockPageScroll: false,
            },
        );
    }

    const checkAuth = (currentUser) => {
        return new Promise((resolve) => {
            if (!currentUser) {
                modalContext?.renderModal(UserAuthenticationModal, {
                    event,
                    $locale,
                    onClose: (props) => resolve(props?.isAuth),
                }, null, true)
            } else {
                resolve(true);
            }
        });
    };

    const onRateToggle = async (ratedStorageKey, episodeId, isRated, ratedStorageValue) => {
        await episodeLikeToggle(episodeId, isRated);
        storage.set(ratedStorageKey, ratedStorageValue, storage.DAY * 30);
    }

    if (!event || !currentStream?.episode?.length || !block.showInStreams?.includes(currentStream.id) || !!embedEpisode) {
        return null;
    }

    const isBookmarkVisible = !isWidget && event?.moduleEpisode?.episodeFavorite;

    return (
        <section className={"page-block episodes-block"}>
            <div className="page-block-header">
                <div className="episodes-block-tabs">
                    <h1
                        onClick={() => setIsActiveFavorites(false)}
                        className={`page-block-title ${!isActiveFavorites ? '-active' : ''}`}
                    >
                        {block.showTitle ? $locale.getProperty(block, 'title') : ''}
                    </h1>
                    {
                        isBookmarkVisible &&
                        <h1
                            onClick={() => setIsActiveFavorites(true)}
                            className={`page-block-title ${isActiveFavorites ? '-active' : ''}`}
                        >
                            {$locale.get('title.favorites')}
                        </h1>
                    }
                </div>
                {!isActiveFavorites && (
                    <SlideControl
                        ref={slideControlRef}
                        targetRef={scheduleRef}
                        slideOffset={slideOffset}
                        setSlideOffset={setSlideOffset}
                    />
                )}
            </div>
            {isActiveFavorites ? (
                <ScheduleContextProvider
                    event={event}
                    page={page}
                >
                    <ScheduleEpisodes isActiveFavorites={isActiveFavorites} onEpisodeClick={handleEpisodeClick} />
                </ScheduleContextProvider>
            ) : (
                <>
                <DateTabs
                    stream={currentStream}
                    streamDate={streamDate}
                    playerEpisode={playerEpisode}
                    setStreamDate={setStreamDate}
                />
                <Schedule
                    ref={scheduleRef}
                    stream={currentStream}
                    timeline={timeline}
                    episodes={dateEpisodes}
                    zoomValue={zoomValue}
                    card={ScheduleCards}
                    pageType={page.type}
                    setSlideOffset={setSlideOffset}
                    onEpisodeClick={handleEpisodeClick}
                    onScheduleMove={setIsScheduleMove}
                    handleBookmark={handleBookmark}
                    bookmarkEpisodeIds={bookmarkEpisodeIds}
                    isBookmarkVisible={isBookmarkVisible}
                    currentUser={user}
                    checkAuth={checkAuth}
                    onRateEpisode={event.moduleEpisode?.like ? onRateToggle : undefined}
                >
                    <Progress
                        ref={progressRef}
                        timeSize={zoomValue}
                        startTime={timeline?.timeStart}
                        endTime={dateEpisodes?.slice(-1)?.[0]?.timeStop ?? null}
                        onUpdate={handleUpdateProgress}
                        style={{ bottom: 10 }}
                    />
                </Schedule>
                <div className="page-block-footer">
                    {
                        (streams.length > 1 && block.showScheduleButton && !isWidget) &&
                        <CButton
                            label={$locale.getProperty(block, 'scheduleButtonTitle')}
                            size="lg"
                            icon="calendar"
                            className="episodes-block-view-all"
                            onClick={() => navigate(`/event/${watchKey}/program`)}
                        />
                    }
                    <ZoomControl
                        ref={zoomControlRef}
                        zoomValue={zoomValue}
                        minuteSize={block?.pixelsInMinute ?? MINUTE_SIZE}
                        className="episodes-block-zoom"
                        setZoomValue={setZoomValue}
                    />
                </div>
                </>
            )}
        </section>
    )
};
